import type { DashboardAssistantDockMessage } from '../../../../contexts/DashboardAssistantDockContext';
import { getAssistantThread } from '../../../../api/dashboardAssistantHistory';
import { assistantEntryToDockMessage, getDockMessageKey } from './messageModel';
import { transientPollDelayMs } from './transientPollError';

export function getAutoExecutableNavigationPath(
  messages: DashboardAssistantDockMessage[],
  userEntryId: string,
) {
  const navigationMessage = messages.find((message) => {
    if (!message.id?.includes(userEntryId)) {
      return false;
    }
    const payload = message.displayPayload;
    return (
      payload?.renderer === 'dashboard_navigation' &&
      payload.data?.autoExecute === true &&
      typeof payload.data?.path === 'string'
    );
  });

  return typeof navigationMessage?.displayPayload?.data?.path === 'string'
    ? navigationMessage.displayPayload.data.path
    : null;
}

export async function pollForPersistedAssistantResponse({
  knownAssistantMessageKeys = [],
  organizationId,
  threadId,
  userEntryId,
}: {
  knownAssistantMessageKeys?: string[];
  organizationId: string;
  threadId: string;
  userEntryId: string;
}) {
  const knownAssistantKeys = new Set(knownAssistantMessageKeys);
  const maxAttempts = 45;
  let transientFailures = 0;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const delayMs = Math.min(600 + attempt * 150, 1800);
    let backoff: number | null = null;
    try {
      const result = await getAssistantThread({
        threadId,
        organizationId,
        limit: 200,
      });
      transientFailures = 0;
      const userEntry = result.entries.find((entry) => entry.entryId === userEntryId);
      const userSequence = userEntry?.sequence;
      const messages = result.entries.map(assistantEntryToDockMessage);
      const hasAssistantAfterUser =
        typeof userSequence === 'number' &&
        messages.some(
          (message) =>
            message.role === 'assistant' &&
            (message.kind === 'message' || message.actionType === 'error') &&
            message.content.trim().length > 0 &&
            typeof message.sequence === 'number' &&
            message.sequence > userSequence,
        );
      const hasNewAssistantMessage = messages.some(
        (message) =>
          message.role === 'assistant' &&
          (message.kind === 'message' || message.actionType === 'error') &&
          message.content.trim().length > 0 &&
          !knownAssistantKeys.has(getDockMessageKey(message)),
        );

      if (hasAssistantAfterUser || hasNewAssistantMessage) {
        return messages;
      }
    } catch (error) {
      // A 429/503 is a busy server, not a failed send: wait and poll again.
      backoff = transientPollDelayMs(error, transientFailures + 1);
      if (backoff !== null) transientFailures += 1;
      const message = error instanceof Error ? error.message : String(error || '');
      if (backoff === null && !/not found|NOT_FOUND/i.test(message)) {
        throw error;
      }
    }

    await wait(backoff ?? delayMs);
  }

  throw new Error('The assistant is still working. Refresh this chat in a moment.');
}

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export function getAssistantSendError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || '');
  if (/HTTP\s+503|Service Unavailable/i.test(message)) {
    return new Error(
      'The assistant provider is temporarily unavailable. Please try sending your message again in a moment.',
    );
  }

  return error instanceof Error ? error : new Error('Chat failed to send.');
}
